/**
 * TopBar.jsx — Sticky top navigation bar for the heatmap dashboard.
 * Shows: logo, overall status pill, current phase, docs/min, health.
 */

export default function TopBar({ progress, health }) {
  const { total_docs = 0, steps = {} } = progress || {};

  // Determine overall status from step statuses
  const statuses = Object.values(steps).map((s) => s.status);
  let overallStatus = "idle";
  if (statuses.includes("running"))       overallStatus = "running";
  else if (statuses.includes("paused"))   overallStatus = "paused";
  else if (statuses.every((s) => s === "done" || s === "idle") && total_docs > 0)
    overallStatus = statuses.every((s) => s === "done") ? "done" : "idle";

  // Active step label
  const activeStep = ["scrape", "enrich", "score"].find(
    (s) => steps[s]?.status === "running"
  );
  const stepLabels = { scrape: "Scraping", enrich: "Enriching", score: "Scoring" };

  // Docs/min from the running step
  const activeRate = activeStep ? steps[activeStep]?.rate ?? 0 : 0;

  const totalDone = (steps.scrape?.done || 0) + (steps.enrich?.done || 0) + (steps.score?.done || 0);
  const totalOps  = total_docs * 3;

  return (
    <header className="hm-topbar">
      <div className="hm-topbar__logo">
        SAGD <span>Heatmap</span>
      </div>

      <div className="hm-topbar__sep" />

      <div
        className={`hm-topbar__pill hm-topbar__pill--${overallStatus}`}
        title="Overall pipeline status"
      >
        <span className={`hm-status-dot hm-status-dot--${overallStatus}`} />
        {overallStatus === "running" ? (activeStep ? stepLabels[activeStep] : "Running") : capitalize(overallStatus)}
      </div>

      {total_docs > 0 && (
        <>
          <div className="hm-topbar__sep" />
          <div className="hm-topbar__item">
            <span>{total_docs.toLocaleString()}</span>
            <span>docs</span>
          </div>
        </>
      )}

      {activeRate > 0 && (
        <>
          <div className="hm-topbar__sep" />
          <div className="hm-topbar__item">
            <strong>{activeRate.toFixed(0)}</strong>
            <span>docs/min</span>
          </div>
        </>
      )}

      {totalOps > 0 && (
        <>
          <div className="hm-topbar__sep" />
          <div className="hm-topbar__item">
            <strong>{Math.round(totalDone / totalOps * 100)}%</strong>
            <span>overall</span>
          </div>
        </>
      )}

      <div className="hm-topbar__spacer" />

      {health && (
        <div className="hm-topbar__item" title="Redis connection">
          <span
            className="hm-status-dot"
            style={{ background: health.redis ? "#0F7E6D" : "#EF4444" }}
          />
          <span style={{ fontSize: 11 }}>Redis</span>
        </div>
      )}

      <div className="hm-topbar__sep" />
      <div className="hm-topbar__item" style={{ fontSize: 11, color: "#4B5563" }}>
        {new Date().toLocaleTimeString()}
      </div>
    </header>
  );
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}
